import React, { useMemo } from 'react';
import { useDashboard } from '../contexts/DashboardContext';
import { usePermissions } from '../hooks/usePermissions';
import type { ToolType } from '../types/dashboard';
import {
  TOOL_PERMISSIONS,
  TOOL_CATEGORIES,
  TOOL_CATEGORY_LABELS,
  getToolBadge,
  getToolSortDate,
} from '../config/permissions';
import type { ToolCategory, ToolPermissionConfig } from '../config/permissions';
import { WidgetBadge } from './WidgetBadge';

// Helper to get display name for required permission
function getPermissionDisplayName(_tool: (typeof TOOL_PERMISSIONS)[number], accessInfo: ReturnType<ReturnType<typeof usePermissions>['getToolAccessInfo']>): string {
  return accessInfo.requiredPermission?.displayName || 'Premium';
}

interface AddToolModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddToolModal: React.FC<AddToolModalProps> = ({ isOpen, onClose }) => {
  const { addWindow } = useDashboard();
  const { getToolAccessInfo, isToolVisible } = usePermissions();

  // Filter to visible tools, then group into sections.
  //
  // `hiddenFromCatalog` is checked HERE and nowhere else, deliberately — it
  // stops a tool being added without touching dashboards that already have one.
  //
  // Sections are built from TOOL_CATEGORIES rather than from whatever the tools
  // happen to declare, so the order is fixed and an empty section simply does
  // not render. `adminOnly` decides the admin group regardless of `category`,
  // which keeps admin tools at the bottom even if one is mislabelled.
  const sections = useMemo(() => {
    const visible = TOOL_PERMISSIONS.filter(
      (tool) => isToolVisible(tool.toolType) && !tool.hiddenFromCatalog
    );

    const byCategory = new Map<ToolCategory, ToolPermissionConfig[]>();
    for (const tool of visible) {
      const key: ToolCategory = tool.adminOnly ? 'admin' : tool.category ?? 'misc';
      const bucket = byCategory.get(key);
      if (bucket) bucket.push(tool);
      else byCategory.set(key, [tool]);
    }

    return TOOL_CATEGORIES.map((category) => ({
      category,
      label: TOOL_CATEGORY_LABELS[category],
      // Newest first within a section, as the flat list was before.
      tools: (byCategory.get(category) ?? []).sort(
        (a, b) => getToolSortDate(b.toolType) - getToolSortDate(a.toolType)
      ),
    })).filter((section) => section.tools.length > 0);
  }, [isToolVisible]);

  if (!isOpen) return null;

  const handleAddTool = (toolType: ToolType) => {
    addWindow(toolType);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-dark-surface border border-dark-border rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
          <h2 className="text-lg font-semibold text-gray-100">Add New Tool</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {sections.map((section) => (
            <div key={section.category}>
              {/* Section heading, with a rule filling the remaining width so a
                  short label reads as a divider rather than a floating word. */}
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[1.5px] whitespace-nowrap">
                  {section.label}
                </h3>
                <div className="h-px flex-1 bg-dark-border" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {section.tools.map((tool) => {
                  const accessInfo = getToolAccessInfo(tool.toolType);
                  const isLocked = !accessInfo.hasAccess;
                  const permissionName = getPermissionDisplayName(tool, accessInfo);
                  const badge = getToolBadge(tool.toolType);

                  return (
                    <button
                      key={tool.toolType}
                      onClick={() => handleAddTool(tool.toolType)}
                      className={`group relative flex flex-col items-start p-4 bg-dark-bg border border-dark-border rounded-lg transition-colors text-left cursor-pointer ${isLocked ? 'opacity-75 hover:opacity-90 hover:bg-dark-surface-light' : 'hover:bg-dark-surface-light'}`}
                      title={isLocked ? `Requires ${permissionName}` : ''}
                    >
                      {/* Badge for admin/beta/new/updated tools - top left */}
                      {badge && (
                        <div className="absolute top-2 left-2">
                          <WidgetBadge type={badge.type} size="md" />
                        </div>
                      )}

                      {/* Crown icon for restricted tools - top right */}
                      {isLocked && (
                        <div className="absolute top-2 right-2">
                          <svg
                            className="w-4 h-4 text-accent-yellow"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M2.5 19h19v2h-19v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06l-4.23 1.12-3.47-5.46a1.5 1.5 0 00-2.56 0L6.5 9.7l-4.23-1.12c-.8-.22-1.63.26-1.84 1.06-.21.8.26 1.63 1.06 1.84l4.53 1.2v6.32h12v-6.32l4.53-1.2c.8-.21 1.27-1.04 1.06-1.84z" />
                          </svg>
                        </div>
                      )}

                      <div
                        className={`text-sm font-medium text-gray-100 mb-1 ${badge ? 'pt-5' : ''} ${isLocked ? 'pr-6' : ''}`}
                      >
                        {tool.displayName}
                      </div>
                      <div className="text-xs text-gray-400">{tool.description}</div>

                      {/* Tooltip on hover for locked tools */}
                      {isLocked && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-dark-surface-light border border-dark-border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                          <div className="text-xs text-gray-300">
                            Requires: <span className="text-accent-yellow font-medium">{permissionName}</span>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
